import { inject, Injectable, OnDestroy, Signal } from '@angular/core';
import { DepositCheckerService } from './deposit-checker.service';
import { Deposit, Deposits } from './deposits';
import { StorageService } from '../storage/storage.service';

@Injectable({
  providedIn: 'root',
})
export class DepositService implements OnDestroy {
  private depositCheckerService = inject(DepositCheckerService);
  private storageService = inject(StorageService);
  private deposits: Deposits | undefined;

  async initialise(userId: string): Promise<void> {
    this.deposits = new Deposits(userId, this.storageService);
    await this.deposits.initialise();
    this.depositCheckerService.setDeposits(this.deposits);
  }

  ngOnDestroy(): void {
    this.depositCheckerService.clearDeposits();
  }

  consumedDeposit(): Signal<Deposit | undefined> {
    return this.depositCheckerService.consumedDeposit$;
  }

  async addDeposit(deposit: Deposit): Promise<void> {
    if (!this.deposits) {
      throw new Error('Deposits not initialized');
    }
    await this.deposits.add(deposit);
    if (this.deposits.getDeposits().length === 1) {
      this.depositCheckerService.start(); // Start DepositChecker if the first deposit is added
    }
  }

  async removeDeposit(deposit: Deposit): Promise<void> {
    if (!this.deposits) {
      throw new Error('Deposits not initialized');
    }
    await this.deposits.remove(deposit);
    if (this.deposits.getDeposits().length === 0) {
      this.depositCheckerService.stop(); // Stop DepositChecker if all deposits are removed
    }
  }

  async removeInvoice(invoice: string): Promise<void> {
    if (!this.deposits) {
      throw new Error('Deposits not initialized');
    }
    await this.deposits.removeInvoice(invoice);
    if (this.deposits.getDeposits().length === 0) {
      this.depositCheckerService.stop(); // Stop DepositChecker if all deposits are removed
    }
  }

  depositCount(): number {
    return this.deposits ? this.deposits.getDeposits().length : 0;
  }
}
